// src/screens/Community.js — Drift community moderation console (June 2026).
//
// Moderates the drift_* family the native app actually uses (NOT the legacy
// community_* tables). Report-driven by design: DM bodies surface ONLY via
// member-filed reports — there is no DM browser, deliberately.
//
// Data:    admin_drift_overview / _messages / _members / _layovers / _reports
// Actions: admin_drift_hide / _unhide (soft-hide, RLS-enforced for clients),
//          admin_drift_set_member_status (suspended members cannot post —
//          enforced by restrictive RLS policies, no app build needed),
//          admin_drift_resolve_report. All actions audit-log server-side.

import React, { useState, useEffect, useCallback } from 'react';
import {
  ConsoleShell, KPI, Panel, SectionLabel, Bar, StatLine, Empty, Pill, Table,
  Drawer, SkeletonRows, useConsoleTheme, useIsMobile, F,
} from '../components/Console';
import { supabase } from '../supabaseClient';
import { getUserRole } from '../roles';

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());
const day = s => (s ? String(s).slice(0, 10) : '—');
const ago = s => {
  if (!s) return '—';
  const m = Math.round((Date.now() - new Date(s).getTime()) / 60000);
  if (m < 1) return 'now';
  if (m < 60) return m + 'm';
  if (m < 1440) return Math.round(m / 60) + 'h';
  return Math.round(m / 1440) + 'd';
};

function statusTone(st) {
  if (st === 'active') return 'up';
  if (st === 'suspended') return 'danger';
  return undefined;
}

function ActionBtn({ bg, disabled, onClick, children, ghost, t }) {
  if (ghost) {
    return (
      <button onClick={onClick} disabled={disabled}
        style={{ fontSize: '0.84rem', padding: '6px 12px', borderRadius: 7, fontWeight: 500, fontFamily: F,
          background: t.period, color: t.tx, border: `1px solid ${t.bd}`, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
        {children}
      </button>
    );
  }
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ fontSize: '0.84rem', padding: '6px 12px', borderRadius: 7, border: 'none', fontWeight: 600, fontFamily: F,
        background: disabled ? t.gbar : bg, color: disabled ? t.mut : '#fff', cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {children}
    </button>
  );
}

// Drawer body for a piece of content (channel message or layover)
function ContentInspector({ item, kind, canMod, onAction }) {
  const { t } = useConsoleTheme();
  const [reason, setReason] = useState('');
  const [working, setWorking] = useState(null);
  const [msg, setMsg] = useState(null);
  const hidden = !!item.hidden_at;
  const authorSuspended = item.author_status === 'suspended';

  const run = async (fn, label) => {
    setWorking(label); setMsg(null);
    try {
      const { error } = await fn();
      if (error) throw error;
      setMsg({ ok: true, text: label + ' done.' });
      onAction && onAction();
    } catch (_e) { setMsg({ ok: false, text: label + ' failed: ' + _e.message }); }
    setWorking(null);
  };

  const hide = () => run(() => supabase.rpc('admin_drift_hide', { p_target_type: kind, p_id: item.id, p_reason: reason.trim() || null }), 'Hide');
  const unhide = () => run(() => supabase.rpc('admin_drift_unhide', { p_target_type: kind, p_id: item.id }), 'Restore');
  const suspend = () => run(() => supabase.rpc('admin_drift_set_member_status', { p_user_id: item.user_id || item.author_user_id, p_status: 'suspended', p_reason: reason.trim() || ('via ' + kind + ' moderation') }), 'Suspend author');
  const restore = () => run(() => supabase.rpc('admin_drift_set_member_status', { p_user_id: item.user_id || item.author_user_id, p_status: 'active', p_reason: null }), 'Restore author');

  const authorId = item.user_id || item.author_user_id;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <Pill tone={hidden ? 'danger' : 'up'}>{hidden ? 'hidden' : 'visible'}</Pill>
        {item.is_sana && <Pill>Sana</Pill>}
        {authorSuspended && <Pill tone='danger'>author suspended</Pill>}
      </div>
      <div style={{ fontSize: '0.95rem', color: t.tx, background: t.rowbg, border: `1px solid ${t.bd}`, borderRadius: 8, padding: '10px 12px', lineHeight: 1.55, marginBottom: 12, whiteSpace: 'pre-wrap' }}>
        {kind === 'layover' ? ((item.title || '') + (item.body ? '\n' + item.body : '')) : (item.body || '—')}
      </div>
      <StatLine label='Author' value={item.author || '—'} />
      {item.email && <StatLine label='Email' value={item.email} />}
      {item.channel && <StatLine label='Channel' value={item.channel} />}
      {item.city && <StatLine label='City' value={[item.city, item.country].filter(Boolean).join(', ')} />}
      <StatLine label='Posted' value={day(item.created_at) + ' · ' + ago(item.created_at) + ' ago'} />
      {hidden && <StatLine label='Hidden by' value={(item.hidden_by || '—') + (item.hidden_reason ? ' · ' + item.hidden_reason : '')} color={t.danger} />}

      <div style={{ height: 1, background: t.bd, margin: '12px 0' }} />
      {!canMod ? <Empty>Your role is read-only for moderation actions.</Empty> : (
        <div>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder='Reason (logged to the audit trail)'
            style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.9rem', padding: '6px 9px', borderRadius: 6, border: `1px solid ${t.bd}`, background: t.panel, color: t.tx, outline: 'none', fontFamily: F, marginBottom: 10 }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!item.is_sana && (hidden
              ? <ActionBtn t={t} ghost disabled={working !== null} onClick={unhide}>{working === 'Restore' ? '…' : 'Restore content'}</ActionBtn>
              : <ActionBtn t={t} bg={t.danger} disabled={working !== null} onClick={hide}>{working === 'Hide' ? '…' : 'Hide content'}</ActionBtn>)}
            {authorId && !item.is_sana && (authorSuspended
              ? <ActionBtn t={t} ghost disabled={working !== null} onClick={restore}>{working === 'Restore author' ? '…' : 'Restore author'}</ActionBtn>
              : <ActionBtn t={t} bg='#fd7e14' disabled={working !== null} onClick={suspend}>{working === 'Suspend author' ? '…' : 'Suspend author'}</ActionBtn>)}
          </div>
        </div>
      )}
      {msg && (
        <div style={{ marginTop: 10, padding: '7px 11px', borderRadius: 8, fontSize: '0.88rem',
          background: msg.ok ? t.live : t.alert, color: msg.ok ? t.livec : t.alertc, border: `1px solid ${msg.ok ? t.bd : t.alertbd}` }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

// Drawer body for a member
function MemberInspector({ m, canMod, onAction }) {
  const { t } = useConsoleTheme();
  const [reason, setReason] = useState('');
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState(null);
  const suspended = m.status === 'suspended';

  const setStatus = async (status) => {
    setWorking(true); setMsg(null);
    try {
      const { error } = await supabase.rpc('admin_drift_set_member_status', { p_user_id: m.user_id, p_status: status, p_reason: reason.trim() || null });
      if (error) throw error;
      setMsg({ ok: true, text: status === 'suspended' ? 'Member suspended — posting is blocked at the database level.' : 'Member restored.' });
      onAction && onAction();
    } catch (_e) { setMsg({ ok: false, text: 'Failed: ' + _e.message }); }
    setWorking(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: '1.1rem', fontWeight: 600, color: t.tx }}>{m.display_name || m.handle || 'Member'}</span>
        <Pill tone={statusTone(m.status)}>{m.status}</Pill>
      </div>
      <div style={{ fontSize: '0.88rem', color: t.mut, marginBottom: 12 }}>{m.email || m.user_id}</div>
      <StatLine label='Member N°' value={m.member_number != null ? '#' + m.member_number : '—'} />
      {m.handle && <StatLine label='Handle' value={'@' + m.handle} />}
      {m.home_country && <StatLine label='Home' value={m.home_country} />}
      <StatLine label='Joined' value={day(m.joined_at)} />
      <StatLine label='Last seen' value={m.last_seen_at ? ago(m.last_seen_at) + ' ago' : '—'} />
      <StatLine label='Messages' value={fmt(m.messages)} />
      <StatLine label='Layovers' value={fmt(m.layover_count)} />
      <StatLine label='Helped count' value={fmt(m.helped_count)} />
      {m.open_reports_against > 0 && <StatLine label='Open reports against' value={fmt(m.open_reports_against)} color={t.danger} />}
      <div style={{ height: 1, background: t.bd, margin: '12px 0' }} />
      {!canMod ? <Empty>Your role is read-only for moderation actions.</Empty> : (
        <div>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder='Reason (logged to the audit trail)'
            style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.9rem', padding: '6px 9px', borderRadius: 6, border: `1px solid ${t.bd}`, background: t.panel, color: t.tx, outline: 'none', fontFamily: F, marginBottom: 10 }} />
          {suspended
            ? <ActionBtn t={t} ghost disabled={working} onClick={() => setStatus('active')}>{working ? '…' : 'Restore member'}</ActionBtn>
            : <ActionBtn t={t} bg={t.danger} disabled={working} onClick={() => setStatus('suspended')}>{working ? '…' : 'Suspend member'}</ActionBtn>}
          <div style={{ fontSize: '0.78rem', color: t.mut, marginTop: 6 }}>
            Suspension hides the member from the directory and blocks all posting (channels, DMs, layovers) via RLS — takes effect immediately, no app update needed.
          </div>
        </div>
      )}
      {msg && (
        <div style={{ marginTop: 10, padding: '7px 11px', borderRadius: 8, fontSize: '0.88rem',
          background: msg.ok ? t.live : t.alert, color: msg.ok ? t.livec : t.alertc, border: `1px solid ${msg.ok ? t.bd : t.alertbd}` }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

// One report row with inline resolution actions
function ReportRow({ r, canMod, onAction }) {
  const { t } = useConsoleTheme();
  const [working, setWorking] = useState(null);
  const open = r.status === 'open';

  const resolve = async (status, note) => {
    setWorking(status);
    try {
      const { error } = await supabase.rpc('admin_drift_resolve_report', { p_report_id: r.id, p_status: status, p_note: note || null });
      if (error) throw error;
      onAction && onAction();
    } catch (_e) { /* surfaces on reload */ }
    setWorking(null);
  };
  const hideAndAction = async () => {
    setWorking('hide');
    try {
      if (r.target_type !== 'dm_message' && r.target_type !== 'member') {
        const kind = r.target_type === 'message' ? 'message' : r.target_type;
        await supabase.rpc('admin_drift_hide', { p_target_type: kind, p_id: r.target_id, p_reason: 'report ' + r.id });
      }
      if (r.content_author_id && (r.target_type === 'dm_message' || r.target_type === 'member')) {
        await supabase.rpc('admin_drift_set_member_status', { p_user_id: r.content_author_id, p_status: 'suspended', p_reason: 'report ' + r.id });
      }
      await supabase.rpc('admin_drift_resolve_report', { p_report_id: r.id, p_status: 'actioned', p_note: r.target_type === 'dm_message' || r.target_type === 'member' ? 'author suspended' : 'content hidden' });
      onAction && onAction();
    } catch (_e) { /* surfaces on reload */ }
    setWorking(null);
  };

  return (
    <div style={{ padding: '10px 0', borderBottom: `1px solid ${t.bd}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
        <Pill tone={open ? 'danger' : undefined}>{r.status}</Pill>
        <Pill tone='warn'>{r.target_type.replace('_', ' ')}</Pill>
        <span style={{ fontSize: '0.85rem', color: t.mut }}>reported by {r.reporter || 'member'} · {ago(r.created_at)} ago</span>
      </div>
      {r.reason && <div style={{ fontSize: '0.9rem', color: t.tx, marginBottom: 5 }}><b style={{ fontWeight: 600 }}>Reason:</b> {r.reason}</div>}
      {r.content && (
        <div style={{ fontSize: '0.88rem', color: t.tx, background: t.rowbg, border: `1px solid ${t.bd}`, borderRadius: 7, padding: '7px 10px', marginBottom: 7, lineHeight: 1.5 }}>
          {r.content}
        </div>
      )}
      {open && canMod && (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          <ActionBtn t={t} bg={t.danger} disabled={working !== null} onClick={hideAndAction}>
            {working === 'hide' ? '…' : (r.target_type === 'dm_message' || r.target_type === 'member' ? 'Suspend author & resolve' : 'Hide content & resolve')}
          </ActionBtn>
          <ActionBtn t={t} ghost disabled={working !== null} onClick={() => resolve('dismissed', 'no action needed')}>
            {working === 'dismissed' ? '…' : 'Dismiss'}
          </ActionBtn>
        </div>
      )}
      {!open && <div style={{ fontSize: '0.8rem', color: t.mut }}>resolved by {r.resolved_by || '—'} · {r.resolution_note || ''}</div>}
    </div>
  );
}

function CommunityBody() {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const [ov, setOv] = useState(null);
  const [messages, setMessages] = useState(null);
  const [members, setMembers] = useState(null);
  const [layovers, setLayovers] = useState(null);
  const [reports, setReports] = useState(null);
  const [chFilter, setChFilter] = useState('all');
  const [sel, setSel] = useState(null);       // { kind: 'message'|'layover'|'member', item }
  const [myRole, setMyRole] = useState('support');

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setMyRole(getUserRole((data && data.user && data.user.email) || ''));
    });
  }, []);
  const canMod = myRole === 'admin' || myRole === 'support';

  const load = useCallback(async () => {
    try {
      const [o, m, mem, lay, rep] = await Promise.all([
        supabase.rpc('admin_drift_overview'),
        supabase.rpc('admin_drift_messages', { p_limit: 300 }),
        supabase.rpc('admin_drift_members', { p_limit: 500 }),
        supabase.rpc('admin_drift_layovers', { p_limit: 100 }),
        supabase.rpc('admin_drift_reports'),
      ]);
      setOv(o.data || {});
      setMessages(Array.isArray(m.data) ? m.data : []);
      setMembers(Array.isArray(mem.data) ? mem.data : []);
      setLayovers(Array.isArray(lay.data) ? lay.data : []);
      setReports(Array.isArray(rep.data) ? rep.data : []);
    } catch (_e) {
      setOv({}); setMessages([]); setMembers([]); setLayovers([]); setReports([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const loading = ov == null;
  const o = ov || {};
  const byChannel = o.by_channel || [];
  const maxCh = Math.max(1, ...byChannel.map(c => c.n || 0));
  const openReports = (reports || []).filter(r => r.status === 'open');
  const col = n => (mobile ? (n >= 4 ? '1fr 1fr' : '1fr') : `repeat(${n},1fr)`);

  let msgView = messages || [];
  if (chFilter !== 'all') msgView = msgView.filter(m => m.channel_id === chFilter);

  const filterBtn = active => ({
    fontSize: '0.85rem', padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontFamily: F, fontWeight: 500,
    border: `1px solid ${active ? t.accent : t.bd}`, background: active ? t.accent + '22' : t.period, color: active ? t.accent : t.mut,
  });

  const msgCols = [
    { label: 'When', render: r => ago(r.created_at), muted: true, sort: r => r.created_at || '', text: r => day(r.created_at) },
    { label: 'Channel', key: 'channel', muted: true },
    { label: 'Author', render: r => (r.is_sana ? 'Sana ✦' : (r.author || '—')), sort: r => r.author || '', text: r => r.author || '' },
    { label: 'Message', render: r => ((r.body || '').length > 64 ? (r.body || '').slice(0, 62) + '…' : (r.body || '—')), text: r => r.body || '' },
    { label: 'State', render: r => (r.hidden_at ? <Pill tone='danger'>hidden</Pill> : <Pill tone='up'>visible</Pill>), text: r => (r.hidden_at ? 'hidden' : 'visible') },
  ];
  const memberCols = [
    { label: 'N°', render: r => (r.member_number != null ? '#' + r.member_number : '—'), mono: true, sort: r => r.member_number || 0, text: r => String(r.member_number || '') },
    { label: 'Member', render: r => r.display_name || r.handle || '—', sort: r => r.display_name || '', text: r => r.display_name || r.handle || '' },
    { label: 'Email', render: r => (r.email ? (r.email.length > 24 ? r.email.slice(0, 22) + '…' : r.email) : '—'), muted: true, text: r => r.email || '' },
    { label: 'Status', render: r => <Pill tone={statusTone(r.status)}>{r.status}</Pill>, sort: r => r.status || '', text: r => r.status || '' },
    { label: 'Msgs', key: 'messages', align: 'right', num: true },
    { label: 'Last seen', render: r => (r.last_seen_at ? ago(r.last_seen_at) : '—'), muted: true, sort: r => r.last_seen_at || '', text: r => day(r.last_seen_at) },
  ];
  const layoverCols = [
    { label: 'When', render: r => ago(r.created_at), muted: true, sort: r => r.created_at || '', text: r => day(r.created_at) },
    { label: 'Title', render: r => ((r.title || '').length > 44 ? (r.title || '').slice(0, 42) + '…' : (r.title || '—')), text: r => r.title || '' },
    { label: 'City', render: r => [r.city, r.country].filter(Boolean).join(', ') || '—', muted: true, text: r => r.city || '' },
    { label: 'Author', key: 'author', muted: true },
    { label: 'Kudos', key: 'kudos', align: 'right', num: true },
    { label: 'State', render: r => (r.hidden_at ? <Pill tone='danger'>hidden</Pill> : <Pill tone='up'>visible</Pill>), text: r => (r.hidden_at ? 'hidden' : 'visible') },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: col(6), gap: 10 }}>
        <KPI label='Members' value={loading ? '…' : fmt(o.members_active)} sub={fmt(o.members_total) + ' total'} accent='#17a86a' />
        <KPI label='Suspended' value={loading ? '…' : fmt(o.members_suspended)} sub='posting blocked' accent={o.members_suspended > 0 ? '#fd7e14' : '#6F8FA7'} />
        <KPI label='Messages · 24h' value={loading ? '…' : fmt(o.messages_24h)} sub={fmt(o.messages_7d) + ' in 7d'} accent={t.accent} />
        <KPI label='Layovers' value={loading ? '…' : fmt(o.layovers_total)} sub='community posts' accent='#314F5C' />
        <KPI label='Hidden content' value={loading ? '…' : fmt(o.hidden_content)} sub='soft-hidden, reversible' accent='#9F7E4F' />
        <KPI label='Open reports' value={loading ? '…' : fmt(o.reports_open)} sub={o.reports_open > 0 ? 'needs review' : 'all clear'} subColor={o.reports_open > 0 ? t.danger : t.mut} accent={o.reports_open > 0 ? '#dc3545' : '#6F8FA7'} />
      </div>

      <SectionLabel right={loading ? '' : fmt((reports || []).length) + ' recent'}>Reports queue</SectionLabel>
      <Panel>
        {reports == null ? <SkeletonRows n={3} /> : (reports.length === 0
          ? <Empty>No reports filed. Reports arrive here when members flag messages, DMs, layovers, or other members — DM content is visible only through a report, never browsed.</Empty>
          : reports.map(r => <ReportRow key={r.id} r={r} canMod={canMod} onAction={load} />))}
      </Panel>

      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 12, marginTop: 12 }}>
        <Panel title='◳ Messages by channel'>
          {byChannel.length === 0 ? <Empty>No channels yet.</Empty>
            : byChannel.map(c => <Bar key={c.id} label={c.name} value={fmt(c.n)} pct={Math.round(((c.n || 0) / maxCh) * 100)} labelWidth={110} onClick={() => setChFilter(chFilter === c.id ? 'all' : c.id)} hint={'Filter table to #' + c.slug} />)}
        </Panel>
        <Panel title='◔ Posting health'>
          <StatLine label='Messages all-time' value={loading ? '…' : fmt(o.messages_total)} />
          <StatLine label='Channels' value={loading ? '…' : fmt(o.channels)} />
          <StatLine label='Hidden content' value={loading ? '…' : fmt(o.hidden_content)} color={o.hidden_content > 0 ? t.warn : undefined} />
          <StatLine label='Suspended members' value={loading ? '…' : fmt(o.members_suspended)} color={o.members_suspended > 0 ? t.danger : undefined} />
          <div style={{ height: 1, background: t.bd, margin: '10px 0' }} />
          <Empty>Hide is reversible and enforced by RLS — hidden content vanishes from every client instantly without an app update. Suspension blocks channel posts, DMs, layovers and comments.</Empty>
        </Panel>
      </div>

      <SectionLabel right={loading ? '' : fmt(msgView.length) + ' of ' + fmt((messages || []).length)}>Channel messages</SectionLabel>
      <Panel>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          <button onClick={() => setChFilter('all')} style={filterBtn(chFilter === 'all')}>All channels</button>
          {byChannel.map(c => <button key={c.id} onClick={() => setChFilter(c.id)} style={filterBtn(chFilter === c.id)}>#{c.slug}</button>)}
        </div>
        {messages == null ? <SkeletonRows n={4} /> : (
          <Table cols={msgCols} rows={msgView} searchable
            empty='No messages yet — they appear here the moment members start talking in the Lounge.'
            onRowClick={r => setSel({ kind: 'message', item: r })} />
        )}
      </Panel>

      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 12, marginTop: 12 }}>
        <Panel title='◉ Members'>
          {members == null ? <SkeletonRows n={4} /> : (
            <Table cols={memberCols} rows={members} searchable pageSize={10}
              empty='No community members yet.'
              onRowClick={r => setSel({ kind: 'member', item: r })} />
          )}
        </Panel>
        <Panel title='◈ Layovers'>
          {layovers == null ? <SkeletonRows n={4} /> : (
            <Table cols={layoverCols} rows={layovers} searchable pageSize={10}
              empty='No layover posts yet.'
              onRowClick={r => setSel({ kind: 'layover', item: { ...r, body: r.title } })} />
          )}
        </Panel>
      </div>

      <Drawer open={!!sel} onClose={() => setSel(null)} title={sel ? (sel.kind === 'member' ? 'Member' : sel.kind === 'layover' ? 'Layover' : 'Message') : ''} width={420}>
        {sel && sel.kind === 'member' && <MemberInspector m={sel.item} canMod={canMod} onAction={load} />}
        {sel && sel.kind !== 'member' && <ContentInspector item={sel.item} kind={sel.kind} canMod={canMod} onAction={load} />}
      </Drawer>
    </div>
  );
}

export default function Community() {
  return (
    <ConsoleShell title='Community'>
      <CommunityBody />
    </ConsoleShell>
  );
}
